import type { SampleRing } from '../sampleRing.js';
import { num, t } from '../i18n/i18n.js';
import { el } from './dom.js';

/**
 * Two sparklines under the minimap: frame time over the last few seconds, and the ping
 * over the last minute.
 *
 * It replaced a line of text — "120 fps · 177k tris · 1 ms ping" — that could only ever
 * say what the average was. What a player who thinks the game is stuttering wants to know
 * is *whether it is*, and when: a hitch every few seconds averages away to nothing in a
 * number and is a row of spikes on a graph. The same goes for the ping, which the text
 * showed once every four seconds with no memory of the last.
 *
 * Cheap on purpose, because it sits next to the thing it measures. Nothing here runs per
 * frame: `draw` is called a few times a second, it touches two small canvases and three
 * text nodes that are only rewritten when their text changes, and the canvases are sized
 * from a `ResizeObserver` rather than by reading layout when drawing.
 *
 * The series colours are not in this file. Each line takes its colour from the label
 * beside it, which takes it from the stylesheet, so the legend and the line cannot
 * disagree and both follow the car-seeded scheme — see `seriesColor`.
 */

/** Top of the frame-time scale, ms: two 60 Hz frames, so the 60 Hz line sits halfway up. */
const FRAME_SCALE_MS = 1000 / 30;
const FRAME_TARGET_MS = 1000 / 60;
/** The ping scale never shrinks below this, so a steady 3 ms on a LAN draws as flat, not as noise. */
const PING_FLOOR_MS = 100;

interface Sparkline {
  canvas: HTMLCanvasElement;
  context: CanvasRenderingContext2D | null;
  /** CSS pixels, as last measured by the observer. */
  width: number;
  height: number;
  dpr: number;
}

export class PerfGraph {
  readonly root: HTMLElement;

  private readonly fpsLabel: HTMLElement;
  private readonly frameValue: HTMLElement;
  private readonly pingLabel: HTMLElement;
  private readonly pingValue: HTMLElement;
  private readonly trianglesLabel: HTMLElement;
  private readonly frame: Sparkline;
  private readonly ping: Sparkline;

  constructor() {
    this.frame = sparkline('perf-spark perf-spark-frame');
    this.ping = sparkline('perf-spark perf-spark-ping');
    this.fpsLabel = el('span', { class: 'perf-key perf-key-frame' }, '');
    this.frameValue = el('span', { class: 'perf-value' }, '');
    this.pingLabel = el('span', { class: 'perf-key perf-key-ping' }, t('ping'));
    this.pingValue = el('span', { class: 'perf-value' }, '');
    this.trianglesLabel = el('span', { class: 'perf-foot' }, '');
    this.root = el(
      'div',
      { class: 'hud-perf tabular is-hidden' },
      this.fpsLabel,
      this.frameValue,
      this.frame.canvas,
      this.pingLabel,
      this.pingValue,
      this.ping.canvas,
      this.trianglesLabel,
    );

    // Never disconnected: the HUD, and this with it, lives as long as the page.
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => {
        measure(this.frame);
        measure(this.ping);
      });
      observer.observe(this.frame.canvas);
      observer.observe(this.ping.canvas);
    }
  }

  /** Per world, not per frame: the geometry does not change during a race. */
  setTriangles(count: number): void {
    setText(this.trianglesLabel, t('{tris}k tris', { tris: num(Math.round(count / 1000)) }));
  }

  /**
   * `frameTimes` in seconds, newest last; `pings` in ms. Called a few times a second —
   * see the class comment for why not every frame.
   */
  draw(frameTimes: SampleRing, fps: number, pings: SampleRing): void {
    if (frameTimes.length === 0) return;
    this.root.classList.remove('is-hidden');
    // Colours first, while the style is clean: reading them after the text writes below
    // would make the browser recompute style in the middle of this call to answer.
    const frameColor = seriesColor(this.fpsLabel);
    const pingColor = seriesColor(this.pingLabel);
    // The 60 Hz guide is drawn in the quiet ink of the triangle count, and faint.
    const guide = seriesColor(this.trianglesLabel);

    // The newest frames, at one column per CSS pixel; each column is the *worst* frame in
    // its slice, because a stutter is the one thing this graph exists to show and an
    // average would sand it off.
    const columns = Math.max(2, Math.round(this.frame.width));
    const perColumn = frameTimes.length / columns;
    const frameMs = new Array<number>(columns);
    for (let c = 0; c < columns; c++) {
      const from = Math.floor(c * perColumn);
      const to = Math.max(from + 1, Math.floor((c + 1) * perColumn));
      let worst = 0;
      for (let i = from; i < to && i < frameTimes.length; i++) worst = Math.max(worst, frameTimes.at(i));
      frameMs[c] = worst * 1000;
    }
    plot(this.frame, frameMs, FRAME_SCALE_MS, frameColor, guide, FRAME_TARGET_MS);

    const pingMs: number[] = [];
    for (let i = 0; i < pings.length; i++) pingMs.push(pings.at(i));
    plot(this.ping, pingMs, Math.max(PING_FLOOR_MS, pings.max() * 1.25), pingColor, guide, null);

    setText(this.fpsLabel, t('{fps} fps', { fps: Math.round(fps) }));
    // The same frames as the fps, so "120 fps" never sits beside "9.1 ms".
    setText(this.frameValue, t('{ms} ms', { ms: num(fps > 0 ? 1000 / fps : 0, 1) }));
    setText(this.pingValue, pings.length ? t('{ms} ms', { ms: num(Math.round(pings.latest())) }) : '–');
  }
}

function sparkline(className: string): Sparkline {
  const canvas = el('canvas', { class: className, width: 1, height: 1 });
  // The numbers beside it say everything a screen reader needs.
  canvas.setAttribute('aria-hidden', 'true');
  return { canvas, context: canvas.getContext('2d'), width: 0, height: 0, dpr: 1 };
}

/** The only place layout is read, and only when the observer says it changed. */
function measure(line: Sparkline): void {
  line.width = line.canvas.clientWidth;
  line.height = line.canvas.clientHeight;
  line.dpr = Math.min(window.devicePixelRatio ?? 1, 2);
  line.canvas.width = Math.max(1, Math.round(line.width * line.dpr));
  line.canvas.height = Math.max(1, Math.round(line.height * line.dpr));
}

/**
 * A filled sparkline, oldest on the left, clipped at `scale` — a spike past the top stays
 * at the top rather than rescaling the whole graph, so the reference line never moves.
 */
function plot(
  line: Sparkline,
  values: number[],
  scale: number,
  color: string,
  guideColor: string,
  guide: number | null,
): void {
  const ctx = line.context;
  if (!ctx || line.width <= 0 || line.height <= 0) return;
  const { width, height, dpr } = line;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, width, height);

  // Inset by half the stroke so a flat line at zero is not drawn half off the canvas.
  const top = 1;
  const bottom = height - 1;
  const y = (v: number) => bottom - Math.min(1, Math.max(0, v / scale)) * (bottom - top);

  if (guide !== null) {
    ctx.strokeStyle = guideColor;
    ctx.globalAlpha = 0.55;
    ctx.lineWidth = 1;
    ctx.setLineDash([2, 2]);
    ctx.beginPath();
    const gy = Math.round(y(guide)) + 0.5;
    ctx.moveTo(0, gy);
    ctx.lineTo(width, gy);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.globalAlpha = 1;
  }

  if (values.length === 0) return;
  // One sample is a level, not a point: drawn across the whole width until there are two.
  const points = values.length === 1 ? [values[0], values[0]] : values;
  const step = width / (points.length - 1);

  ctx.beginPath();
  ctx.moveTo(0, y(points[0]));
  for (let i = 1; i < points.length; i++) ctx.lineTo(i * step, y(points[i]));
  ctx.lineTo(width, height);
  ctx.lineTo(0, height);
  ctx.closePath();
  ctx.globalAlpha = 0.28;
  ctx.fillStyle = color;
  ctx.fill();

  ctx.globalAlpha = 1;
  ctx.beginPath();
  ctx.moveTo(0, y(points[0]));
  for (let i = 1; i < points.length; i++) ctx.lineTo(i * step, y(points[i]));
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.25;
  ctx.lineJoin = 'round';
  ctx.stroke();
}

/**
 * A colour out of the stylesheet, resolved.
 *
 * The scheme's tokens are `light-dark(…)` expressions, and reading a custom property hands
 * back that expression as text, which a canvas cannot use. An element's *computed* `color`
 * is always a resolved `rgb(…)`, so it is read off the label that carries the colour.
 */
function seriesColor(node: HTMLElement): string {
  return getComputedStyle(node).color;
}

function setText(node: HTMLElement, text: string): void {
  if (node.textContent !== text) node.textContent = text;
}
